import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TrackFunction, useAnalytics } from 'support/helpers/analytics/analytics';

import { FilterValue, PropertyFilter, PropertyFilterType, SearchPropertyFilter } from '../ListTable/types';
import { CheckboxFilter } from './CheckboxFilter';
import { FilterBadges } from './components/FilterBadges/FilterBadges';
import { DateFilter } from './DateFilter';
import { DropdownFilter } from './DropdownFilter';
import { SearchFilter } from './SearchFilter';

type FiltersProps = {
  propertyFilters: Array<PropertyFilter>;
  onFiltersChange: (filters: Record<string, string>) => void;
  filterValues: Array<FilterValue> | undefined;
  totalElements?: number | null;
  clearAllFilters: () => void;
};

const FilterCreator = ({
  track,
  filter,
  onChange,
}: {
  track: TrackFunction;
  filter: PropertyFilter;
  onChange: (filterKey: string, newValue: string) => void;
}) => {
  switch (filter.type) {
    case PropertyFilterType.dropdown:
      if (!filter.dropDownOptions) {
        return null;
      }

      return (
        <DropdownFilter
          className="min-w-44"
          label={filter.label}
          currentValue={filter.value}
          dropdownValues={filter.dropDownOptions.dropdownValues}
          filterKey={filter.key}
          onChange={(_, newValue) => {
            if (filter.eventDetails) {
              track(filter.eventDetails.event, filter.eventDetails.properties);
            }

            onChange(filter.key, newValue);
          }}
          hideValueFromButton
          defaultToFirstValue={filter.dropDownOptions.defaultToFirstValue}
          showAllLabel={filter.dropDownOptions.dropdownDefaultLabel}
          multiple={filter.dropDownOptions.multiple}
        />
      );

    case PropertyFilterType.date: {
      let startDate = filter.value;
      let endDate = startDate;

      if (filter.value?.indexOf(',') > -1) {
        startDate = filter.value.split(',')[0];
        endDate = filter.value.split(',')[1];
      }

      return (
        <DateFilter
          placeholder={filter.placeholder}
          startDate={startDate}
          endDate={endDate}
          endDateLimit={filter.endDateLimit}
          filterKey={filter.key}
          onChange={(_, startDate, endDate) => {
            if (filter.eventDetails) {
              track(filter.eventDetails.event, filter.eventDetails.properties);
            }
            onChange(filter.key, `${startDate},${endDate}`);
          }}
        />
      );
    }

    case PropertyFilterType.checkbox:
      return (
        <CheckboxFilter
          label={filter.label}
          currentValue={filter.value}
          filterKey={filter.key}
          onChange={(_, newValue) => {
            if (filter.eventDetails) {
              track(filter.eventDetails.event, filter.eventDetails.properties);
            }
            onChange(filter.key, newValue);
          }}
        />
      );
    case PropertyFilterType.search:
    default:
      return null;
  }
};

const removeValueFromAppliedFilters = (
  propertyFilters: Array<PropertyFilter>,
  key: string,
  value: string,
): Array<PropertyFilter> =>
  propertyFilters.map((filter) => {
    if (key !== filter.key) {
      return filter;
    }

    if (filter.type === PropertyFilterType.dropdown && filter.dropDownOptions) {
      const newValue = filter.value
        .split(',')
        .filter((v) => v !== value)
        .join(',');

      return {
        ...filter,
        value:
          newValue ||
          (filter.dropDownOptions.defaultToFirstValue ? filter.dropDownOptions.dropdownValues[0].value : ''),
      };
    }

    return filter.type === PropertyFilterType.search || filter.type === PropertyFilterType.hidden
      ? filter
      : { ...filter, value: '' };
  });

const formatValues = (filterValues: Array<FilterValue> | undefined): Record<string, string> =>
  filterValues?.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {}) || {};

export function Filters({
  propertyFilters,
  filterValues,
  onFiltersChange,
  clearAllFilters,
  totalElements,
}: FiltersProps) {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const formattedFilters = useMemo(() => formatValues(filterValues), [filterValues]);
  const search: SearchPropertyFilter | undefined = propertyFilters.find(isSearchFilterGuard);

  const badgesFilters: Array<FilterValue> = useMemo(() => {
    return Object.entries(formattedFilters)
      .map((filter) => {
        const propertyField = propertyFilters.find((propertyFilter) => propertyFilter.key === filter[0]);
        if (!propertyField) {
          return undefined;
        }
        if (
          [PropertyFilterType.search, PropertyFilterType.hidden, PropertyFilterType.checkbox].includes(
            propertyField.type,
          )
        ) {
          return undefined;
        }

        if (propertyField.dropDownOptions?.multiple) {
          return (filter[1] as string).split(',').map((value) => {
            // Check if this exact value exists in dropdown options (could contain semicolons for compound values)
            const matchingOption = propertyField.dropDownOptions?.dropdownValues.find(
              (dropdownValue) => dropdownValue.value === value,
            );

            return {
              key: filter[0],
              value: matchingOption?.value || value,
            };
          });
        }

        return {
          key: filter[0],
          value:
            propertyField.dropDownOptions?.dropdownValues.find((dropdownValue) => dropdownValue.value === filter[1])
              ?.value || filter[1],
        };
      })
      .flat()
      .filter(Boolean) as Array<FilterValue>;
  }, [formattedFilters, propertyFilters]);

  if (!propertyFilters.length) {
    return null;
  }

  return (
    <div className="grid gap-2">
      <div className="flex w-full flex-wrap gap-4">
        {!!search && (
          <SearchFilter
            placeholder={
              typeof totalElements === 'number'
                ? search.placeholder(totalElements)
                : typeof search.placeholder === 'string'
                  ? search.placeholder
                  : t('common:search.placeholder')
            }
            currentValue={filterValues?.find((filter) => filter.key === search.key)?.value || ''}
            filterKey={search.key}
            onChange={(filterKey, newValue) => onFiltersChange({ ...formattedFilters, [filterKey]: newValue })}
          />
        )}

        <div className="flex flex-wrap gap-4">
          {propertyFilters
            .filter(({ type }) => type !== PropertyFilterType.search)
            .map((filter: PropertyFilter) => (
              <FilterCreator
                track={track}
                filter={filter}
                key={filter.key}
                onChange={(filterKey, newValue) => onFiltersChange({ ...formattedFilters, [filterKey]: newValue })}
              />
            ))}
        </div>
      </div>

      <FilterBadges
        reset={clearAllFilters}
        filters={badgesFilters}
        filtersConfig={propertyFilters}
        remove={(key, value) => {
          onFiltersChange(formatValues(removeValueFromAppliedFilters(propertyFilters, key, value)));
        }}
      />
    </div>
  );
}

const isSearchFilterGuard = (filter: PropertyFilter): filter is SearchPropertyFilter => {
  return filter.type === PropertyFilterType.search;
};
